import React, {useReducer} from 'react';

import {Menu} from '@albert-io/atomic';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {CleverMark} from 'client/Clever/CleverMark';
import {CleverNotLoggedInModal, CleverSyncModal} from 'client/Clever/CleverSync';
import {
  CleverSyncContext,
  CleverSyncDispatch,
  CleverSyncReducer,
  initialCleverSyncState
} from 'client/Clever/CleverSyncProvider';
import {getUserQuery} from 'client/Clever/util';
import type {CleverUser} from 'client/Clever/util';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

import {EDGE_MENU_POPPER_PROPS} from '../config/menuPopperProps';

interface Props {
  onNewClass: () => void;
  onNewFolder: () => void;
  trigger: React.ComponentProps<typeof Menu.Trigger>['children'];
}

const CreateNewMenuItems = ({
  onImportFromClever,
  onNewClass,
  onNewFolder,
  trigger
}: Props & {onImportFromClever?: () => void}) => (
  <Menu closeOnItemClick popperProps={EDGE_MENU_POPPER_PROPS} position='bottom-start' positionFixed>
    <Menu.Trigger>{trigger}</Menu.Trigger>
    <Menu.Content>
      <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
        Classrooms
      </Menu.Item>
      {onImportFromClever && (
        <Menu.Item onClick={onImportFromClever}>
          <div className='o-menu__item-icon-container self-center'>
            <CleverMark />
          </div>
          <span>Import From Clever</span>
        </Menu.Item>
      )}
      <Menu.Item leftIconProps={{icon: 'chalkboard', iconStyle: 'regular'}} onClick={onNewClass}>
        New Class
      </Menu.Item>
      <Menu.Item className='mt-x2' readonly textProps={{color: 'secondary', size: 's'}}>
        Classwork
      </Menu.Item>
      <Menu.Item
        leftIconProps={{icon: 'file-lines', iconStyle: 'regular'}}
        onClick={() => history.pushState(null, '/assignment-builder/create-assignment')}
      >
        Assignment
      </Menu.Item>
      <Menu.Item
        data-testid='assignments_new-folder-btn'
        leftIconProps={{icon: 'folder', iconStyle: 'regular'}}
        onClick={onNewFolder}
      >
        Folder
      </Menu.Item>
    </Menu.Content>
  </Menu>
);

// Same flow as the legacy classes page's Import & Sync button: open the sync
// modal for Clever-authed users, otherwise prompt to log back in with Clever.
const CreateNewMenuWithClever = ({user, ...menuProps}: Props & {user: CleverUser}) => {
  const [state, dispatch] = useReducer(CleverSyncReducer, user, initialCleverSyncState);

  const handleImportFromClever = () => {
    dispatch({
      type: state.userCleverId && state.userCleverDistrictId ? 'openModal' : 'openLogoutModal'
    });
  };

  return (
    <CleverSyncContext.Provider value={state}>
      <CleverSyncDispatch.Provider value={dispatch}>
        <CreateNewMenuItems onImportFromClever={handleImportFromClever} {...menuProps} />
        {state.isModalOpen && <CleverSyncModal />}
        {state.isLogoutModalOpen && <CleverNotLoggedInModal />}
      </CleverSyncDispatch.Provider>
    </CleverSyncContext.Provider>
  );
};

export const CreateNewMenu = (props: Props) => {
  // Clever gating mirrors the legacy classes page: user meta opt-in, never while masquerading.
  // Skip the query entirely when masquerading — a school admin can't read another user's users_v2
  // record (403), and Import-from-Clever is hidden during masquerade anyway.
  const isMasquerading = masqueradeStore.isMasquerading();
  const {isReady} = useAreLatestQueriesReady(
    isMasquerading ? [] : [() => getUserQuery() as unknown as QueryBuilder]
  );
  const user =
    !isMasquerading && isReady ? (getUserQuery().getResource() as unknown as CleverUser) : null;
  const showCleverImport = Boolean(user?.getMeta().isAllowCleverSync());

  if (user && showCleverImport) {
    return <CreateNewMenuWithClever user={user} {...props} />;
  }
  return <CreateNewMenuItems {...props} />;
};
